import React, { useState } from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowUpRightIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { formatNumber } from '../../utils/formatters';
import { useP2POrders, useMyAds } from '../../hooks/useP2P';
import { toast } from 'react-toastify';
import P2PService from '../../services/p2pService';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/useAuthStore';

const P2PMyOrders = ({ showAds = false }) => {
  const [activeTab, setActiveTab] = useState(showAds ? 'all-ads' : 'active');
  const [page, setPage] = useState(1);
  
  // Fetch real data from backend
  const { orders, loading: ordersLoading, error: ordersError } = useP2POrders();
  const { ads, loading: adsLoading, error: adsError, updateAd, deleteAd } = useMyAds();

  // Calculate real counts
  const activeOrders = orders?.filter(o => ['pending', 'paid', 'escrow', 'disputed', 'appeal'].includes(o.status)) || [];
  const completedOrders = orders?.filter(o => o.status === 'completed') || [];
  const cancelledOrders = orders?.filter(o => o.status === 'cancelled') || [];
  const myAds = ads || [];
  const activeAds = ads?.filter(a => a.status === 'active') || [];
  const pausedAds = ads?.filter(a => a.status === 'paused') || [];
  
  // Define tabs based on mode
  const orderTabs = showAds ? [
    { id: 'all-ads', label: 'All Ads', count: myAds.length },
    { id: 'active-ads', label: 'Active', count: activeAds.length },
    { id: 'paused-ads', label: 'Paused', count: pausedAds.length }
  ] : [
    { id: 'active', label: 'Active Orders', count: activeOrders.length },
    { id: 'completed', label: 'Completed', count: completedOrders.length },
    { id: 'cancelled', label: 'Cancelled', count: cancelledOrders.length }
  ];

  // Get current data based on active tab
  const getCurrentData = () => {
    if (showAds) {
      switch(activeTab) {
        case 'all-ads':
          return myAds;
        case 'active-ads':
          return activeAds;
        case 'paused-ads':
          return pausedAds;
        default:
          return myAds;
      }
    } else {
      switch(activeTab) {
        case 'active':
          return activeOrders;
        case 'completed':
          return completedOrders;
        case 'cancelled':
          return cancelledOrders;
        default:
          return [];
      }
    }
  };
  
  const currentData = getCurrentData();
  const totalPages = Math.ceil(currentData.length / 5);
  const currentPage = Math.min(page, Math.max(1, totalPages));
  const visibleData = currentData.slice((currentPage - 1) * 5, currentPage * 5);
  const isLoading = showAds ? adsLoading : ordersLoading;

  return (
    <div>
      {/* Tabs */}
      <div className="flex flex-wrap sm:flex-nowrap space-x-1 mb-4 sm:mb-6 bg-[#010615] p-1 rounded-lg overflow-x-auto">
        {orderTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setPage(1);
            }}
            className={`flex-1 flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-md transition-colors text-xs sm:text-sm whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-blue-700 text-white'
                : 'text-white hover:text-white hover:bg-gray-700'
            }`}
          >
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
            <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs ${
              activeTab === tab.id
                ? 'bg-white/20 text-white'
                : 'bg-gray-600 text-white'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 sm:py-16">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-white text-sm sm:text-base">Loading...</span>
        </div>
      ) : currentData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 sm:py-16">
          <EyeIcon className="h-12 w-12 sm:h-16 sm:w-16 text-white mb-3 sm:mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-white mb-2">
            No {orderTabs.find(t => t.id === activeTab)?.label.toLowerCase()}
          </h3>
          <p className="text-white text-center max-w-md text-sm sm:text-base px-4">
            Your {showAds ? 'advertisements' : 'orders'} will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleData.map(item => (
            showAds ? (
              <AdCard 
                key={item.ad_id} 
                ad={item} 
                onUpdate={updateAd}
                onDelete={deleteAd}
              />
            ) : (
              <OrderCard 
                key={item.order_id} 
                order={item} 
                type={activeTab}
                onUpdate={updateAd}
                onDelete={deleteAd}
              />
            )
          ))}
        </div>
      )}
      {!isLoading && totalPages > 1 && (
        <nav aria-label={showAds ? 'My ads pagination' : 'My orders pagination'} className="flex flex-wrap items-center justify-center gap-4 mt-6">
          <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
          <span className="text-sm text-white">Page {currentPage} of {totalPages}</span>
          <button type="button" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
        </nav>
      )}
    </div>
  );
};

const OrderCard = ({ order, type, onUpdate, onDelete }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [paymentReference, setPaymentReference] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const isAd = type === 'my-ads';
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-white" />;
      case 'paid':
        return <CheckCircleIcon className="h-4 w-4 text-blue-400" />;
      case 'escrow':
        return <ShieldCheckIcon className="h-4 w-4 text-purple-400" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4 text-green-400" />;
      case 'cancelled':
        return <XCircleIcon className="h-4 w-4 text-red-400" />;
      case 'disputed':
        return <ExclamationTriangleIcon className="h-4 w-4 text-orange-400" />;
      case 'appeal':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-400" />;
      default:
        return <InformationCircleIcon className="h-4 w-4 text-white" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending Payment';
      case 'paid':
        return 'Payment Made';
      case 'escrow':
        return 'In Escrow';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'disputed':
        return 'Disputed';
      case 'appeal':
        return 'Under Appeal';
      default:
        return status || 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-white bg-yellow-900/20';
      case 'paid':
        return 'text-blue-400 bg-blue-900/20';
      case 'escrow':
        return 'text-purple-400 bg-purple-900/20';
      case 'completed':
        return 'text-green-400 bg-green-900/20';
      case 'cancelled':
        return 'text-red-400 bg-red-900/20';
      case 'disputed':
        return 'text-orange-400 bg-orange-900/20';
      case 'appeal':
        return 'text-red-400 bg-red-900/20';
      default:
        return 'text-white bg-gray-900/20';
    }
  };
  
  if (isAd) {
    return <AdCard ad={order} onUpdate={onUpdate} onDelete={onDelete} />;
  }

  // Extract data from the backend response
  const asset = order.ad?.asset || 'Unknown';
  const fiatCurrency = order.ad?.fiat_currency || 'USD';
  const adType = order.ad?.type || 'sell';
  const cryptoAmount = order.crypto_amount || 0;
  const fiatAmount = order.fiat_amount || 0;
  const price = order.price || 0;
  const orderId = order.order_id ? order.order_id.toString().slice(0, 8) : 'N/A';
  const paymentMethodName = order.payment_method?.name || 'Not specified';
  const timeRemaining = order.time_remaining_minutes;

  // Determine if current user is buyer or seller
  const currentUserUsername = user?.username || user?.email;
  const buyerUsername = order.buyer_profile?.user?.username || order.buyer_profile?.user?.email;
  const sellerUsername = order.seller_profile?.user?.username || order.seller_profile?.user?.email;
  
  const isUserBuyer = currentUserUsername === buyerUsername;
  const isUserSeller = currentUserUsername === sellerUsername;
  
  // Show the other party's info
  const counterpartyProfile = isUserBuyer ? order.seller_profile : order.buyer_profile;
  const counterpartyName = counterpartyProfile?.nickname || 'Unknown';
  const userRole = isUserBuyer ? 'Buyer' : isUserSeller ? 'Seller' : 'Unknown';

  const handleMarkAsPaid = async () => {
    setIsProcessing(true);
    try {
      await P2PService.markOrderPaid(order.order_id, paymentReference);
      toast.success('Order marked as paid successfully!');
      setShowMarkPaidModal(false);
      setPaymentReference('');
      // Refresh the orders list
      window.location.reload();
    } catch (error) {
      toast.error(error.message || 'Failed to mark order as paid');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!window.confirm('Confirm that you have received payment from the buyer?')) {
      return;
    }
    
    setIsProcessing(true);
    try {
      await P2PService.confirmPayment(order.order_id);
      toast.success('Payment confirmed! Crypto is now in escrow.');
      // Refresh the orders list
      window.location.reload();
    } catch (error) {
      toast.error(error.message || 'Failed to confirm payment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelOrder = async () => {
    setIsProcessing(true);
    try {
      await P2PService.cancelOrder(order.order_id, cancelReason);
      toast.success('Order cancelled successfully');
      setShowCancelModal(false);
      setCancelReason('');
      // Refresh the orders list
      window.location.reload();
    } catch (error) {
      toast.error(error.message || 'Failed to cancel order');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReleaseCrypto = async () => {
    if (!window.confirm('Are you sure you want to release the crypto to the buyer? This action cannot be undone.')) {
      return;
    }
    
    setIsProcessing(true);
    try {
      await P2PService.releaseCrypto(order.order_id);
      toast.success('Crypto released successfully!');
      // Refresh the orders list
      window.location.reload();
    } catch (error) {
      toast.error(error.message || 'Failed to release crypto');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewDetails = () => {
    // Navigate to order details page
    navigate(`/p2p/order/${order.order_id}`);
  };

  const handleOpenChat = () => {
    // Navigate to chat page for this order
    navigate(`/p2p/order/${order.order_id}/chat`);
  };

  return (
    <div className="bg-gray-750 rounded-xl border border-gray-600 p-4 sm:p-6">
      <div className="flex flex-col space-y-4 lg:space-y-0 lg:flex-row lg:items-center lg:justify-between">
        {/* Order Info */}
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3">
            <div className={`flex items-center space-x-2 px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-medium ${
              adType === 'buy' ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'
            }`}>
              <span>{adType === 'buy' ? 'Buy' : 'Sell'}</span>
              <span>{asset}</span>
            </div>
            
            <div className={`flex items-center space-x-2 px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm ${
              getStatusColor(order.status)
            }`}>
              {getStatusIcon(order.status)}
              <span>{getStatusText(order.status)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-3">
            <div>
              <div className="text-xs text-white mb-1">Order ID</div>
              <div className="text-xs sm:text-sm font-medium text-white truncate">{orderId}</div>
            </div>
            <div>
              <div className="text-xs text-white mb-1">Crypto Amount</div>
              <div className="text-xs sm:text-sm font-medium text-white">
                {formatNumber(cryptoAmount, asset === 'BTC' ? 6 : 2)} {asset}
              </div>
            </div>
            <div>
              <div className="text-xs text-white mb-1">Price</div>
              <div className="text-xs sm:text-sm font-medium text-white">
                {formatNumber(price, 2)} {fiatCurrency}
              </div>
            </div>
            <div>
              <div className="text-xs text-white mb-1">Total</div>
              <div className="text-xs sm:text-sm font-medium text-white">
                {formatNumber(fiatAmount, 2)} {fiatCurrency}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-6 text-xs sm:text-sm text-white">
            <span>Role: <span className="text-white font-medium">{userRole}</span></span>
            <span>Counterparty: <span className="text-white">{counterpartyName}</span></span>
            <span>Payment: <span className="text-white">{paymentMethodName}</span></span>
            {timeRemaining !== null && timeRemaining !== undefined && timeRemaining > 0 && (
              <span className="text-white">⏰ {timeRemaining} min left</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Buyer actions when pending */}
          {order.status === 'pending' && isUserBuyer && (
            <>
              <button 
                onClick={() => setShowMarkPaidModal(true)}
                disabled={isProcessing}
                className="px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Mark as Paid
              </button>
              <button 
                onClick={() => setShowCancelModal(true)}
                disabled={isProcessing}
                className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </>
          )}

          {/* Seller actions when pending */}
          {order.status === 'pending' && isUserSeller && (
            <button 
              onClick={() => setShowCancelModal(true)}
              disabled={isProcessing}
              className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          )}

          {/* Seller action when paid - confirm payment */}
          {order.status === 'paid' && isUserSeller && (
            <button 
              onClick={handleConfirmPayment}
              disabled={isProcessing}
              className="px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Confirming...' : 'Confirm Payment'}
            </button>
          )}
          
          {/* Seller action when in escrow - release crypto */}
          {order.status === 'escrow' && isUserSeller && (
            <button 
              onClick={handleReleaseCrypto}
              disabled={isProcessing}
              className="px-3 sm:px-4 py-2 bg-green-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Releasing...' : 'Release Crypto'}
            </button>
          )}

          {/* Chat and view details for all */}
          <button 
            onClick={handleOpenChat}
            className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            title="Open Chat"
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
          
          <button 
            onClick={handleViewDetails}
            className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            title="View Details"
          >
            <ArrowUpRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>
      </div>

      {/* Mark as Paid Modal */}
      {showMarkPaidModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Mark Order as Paid</h3>
            <p className="text-white mb-4 text-sm">
              Please provide a payment reference or transaction ID for your records:
            </p>
            <input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="Payment reference (optional)"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowMarkPaidModal(false)}
                disabled={isProcessing}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAsPaid}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Cancel Order</h3>
            <p className="text-white mb-4 text-sm">
              Are you sure you want to cancel this order? This action cannot be undone.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation (optional)"
              rows="3"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4 resize-none"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={isProcessing}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Keep Order
              </button>
              <button
                onClick={handleCancelOrder}
                disabled={isProcessing}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isProcessing ? 'Cancelling...' : 'Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdCard = ({ ad, onUpdate, onDelete }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const isActive = ad.status === 'active';

  const handlePauseToggle = async () => {
    setIsUpdating(true);
    try {
      const newStatus = isActive ? 'paused' : 'active';
      await onUpdate(ad.ad_id, { status: newStatus });
      toast.success(`Ad ${newStatus === 'active' ? 'activated' : 'paused'} successfully!`);
    } catch (error) {
      console.error('Error toggling ad status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    setIsUpdating(true);
    try {
      await onDelete(ad.ad_id);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting ad:', error);
    } finally {
      setIsUpdating(false);
    }
  };
  
  return (
    <div className="bg-gray-750 rounded-xl border border-gray-600 p-4 sm:p-6">
      <div className="flex flex-col space-y-4 lg:space-y-0 lg:flex-row lg:items-center lg:justify-between">
        {/* Ad Info */}
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3">
            <div className={`flex items-center space-x-2 px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-medium ${
              ad.type === 'buy' ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'
            }`}>
              <span>{ad.type === 'buy' ? 'Buy' : 'Sell'}</span>
              <span>{ad.asset}</span>
            </div>
            
            <div className={`flex items-center space-x-2 px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm ${
              isActive ? 'bg-green-900/20 text-green-400' : 'bg-gray-700 text-white'
            }`}>
              <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-400' : 'bg-gray-400'}`}></div>
              <span>{isActive ? 'Active' : ad.status || 'Inactive'}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-3">
            <div>
              <div className="text-xs text-white mb-1">Ad ID</div>
              <div className="text-xs sm:text-sm font-medium text-white">{ad.ad_id?.toString().slice(0, 8) || 'N/A'}</div>
            </div>
            <div>
              <div className="text-xs text-white mb-1">Price</div>
              <div className="text-xs sm:text-sm font-medium text-white">
                {formatNumber(ad.price, 4)} {ad.fiat_currency}
              </div>
            </div>
            <div>
              <div className="text-xs text-white mb-1">Available</div>
              <div className="text-xs sm:text-sm font-medium text-white">
                {formatNumber(ad.available_amount, 2)} {ad.asset}
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="text-xs text-white mb-1">Total Amount</div>
              <div className="text-xs sm:text-sm font-medium text-white">
                {formatNumber(ad.total_amount, 2)} {ad.asset}
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="text-xs text-white mb-1">Orders</div>
              <div className="text-xs sm:text-sm font-medium text-white">{ad.completed_orders_count || 0}</div>
            </div>
          </div>

          <div className="text-xs sm:text-sm text-white mb-3 line-clamp-2">
            {ad.terms_and_conditions || 'No terms specified'}
          </div>

          <div className="flex flex-wrap gap-1 sm:gap-2">
            {ad.payment_methods?.map((method, index) => (
              <span 
                key={index}
                className="px-2 sm:px-3 py-1 bg-gray-700 text-white rounded-full text-xs"
              >
                {method.name || method}
              </span>
            )) || <span className="px-2 sm:px-3 py-1 bg-gray-700 text-white rounded-full text-xs">No payment methods</span>}
          </div>
        </div>

        {/* Ad Actions */}
        <div className="flex flex-wrap items-center space-x-2 sm:space-x-3">
          <button 
            onClick={handlePauseToggle}
            disabled={isUpdating}
            className={`px-3 sm:px-4 py-2 rounded-lg transition-colors text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
              isActive 
                ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {isUpdating ? (
              <div className="animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent rounded-full mr-1 sm:mr-2 inline-block"></div>
            ) : isActive ? (
              <PauseIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 inline" />
            ) : (
              <PlayIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 inline" />
            )}
            {isActive ? 'Pause' : 'Activate'}
          </button>
          <button 
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isUpdating}
            className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrashIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 inline" />
            Delete
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Confirm Delete</h3>
            <p className="text-white mb-6">
              Are you sure you want to delete this advertisement? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isUpdating}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isUpdating}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isUpdating ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2 inline-block"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default P2PMyOrders; 